import { addDays, format, isValid, parseISO } from "date-fns";

import {
	TInvoiceLine,
	TInvoiceLineComputed,
	TInvoiceTotals,
	TVatBreakdown,
} from "@/types";

/**
 * Rounds half away from zero to the cent, after settling binary noise
 * (2.5 × 333.33 is 833.3249999999999 in a float). The API applies the
 * same rule (InvoiceTotalsCalculator) and both are tested on the same
 * cases so the form shows what the PDF will print.
 */
export const roundCents = (value: number): number => {
	if (!Number.isFinite(value)) return 0;
	const settled = Math.round(value * 1e10) / 1e10;
	const sign = settled < 0 ? -1 : 1;
	return (sign * Math.round(Math.abs(settled) * 100 + 1e-6)) / 100;
};

const rateOf = (line: TInvoiceLine): number =>
	line.vatApplicable && line.vatRate ? Number(line.vatRate) : 0;

/** Every line's amounts, the totals and the VAT breakdown by rate (ascending). */
export const computeInvoiceTotals = (lines: TInvoiceLine[]): TInvoiceTotals => {
	const byRate = new Map<number, TVatBreakdown>();
	let totalHt = 0;
	let totalVat = 0;

	const computed: TInvoiceLineComputed[] = lines.map((line) => {
		const amountHt = roundCents(
			Number(line.quantity || 0) * Number(line.unitPrice || 0)
		);
		const rate = rateOf(line);
		const amountVat = rate > 0 ? roundCents((amountHt * rate) / 100) : 0;

		totalHt += amountHt;
		totalVat += amountVat;

		if (rate > 0) {
			const row = byRate.get(rate) ?? { rate, base: 0, vat: 0 };
			row.base = roundCents(row.base + amountHt);
			row.vat = roundCents(row.vat + amountVat);
			byRate.set(rate, row);
		}

		return {
			...line,
			amountHt,
			amountVat,
			amountTtc: roundCents(amountHt + amountVat),
		};
	});

	totalHt = roundCents(totalHt);
	totalVat = roundCents(totalVat);

	return {
		totalHt,
		totalVat,
		totalTtc: roundCents(totalHt + totalVat),
		vatBreakdown: [...byRate.values()].sort((a, b) => a.rate - b.rate),
		lines: computed,
	};
};

/** "1 234,50 €" (fr) / "€1,234.50" (en); a malformed code falls back to number + code. */
export const formatMoney = (
	amount: number,
	currency: string = "EUR",
	language: string = "fr"
): string => {
	const value = Number.isFinite(amount) ? amount : 0;
	try {
		return new Intl.NumberFormat(language, {
			style: "currency",
			currency,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(value);
	} catch {
		return `${value.toFixed(2)} ${currency}`;
	}
};

/** A quantity: whole numbers without decimals, otherwise up to two. */
export const formatQuantity = (
	value: number,
	language: string = "fr"
): string =>
	new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(
		Number.isFinite(value) ? value : 0
	);

/** The API's date-only strings ↔ the DatePicker's local-midnight Date. */
export const dateFromApi = (value?: string | null): Date | null => {
	if (!value) return null;
	const date = parseISO(value.slice(0, 10));
	return isValid(date) ? date : null;
};

export const dateToApi = (value: Date | null): string =>
	value && isValid(value) ? format(value, "yyyy-MM-dd") : "";

/** Due date from an issue date and the issuer's terms. */
export const dueDateFor = (issuedAt: string, termsDays: number): string => {
	const date = dateFromApi(issuedAt);
	return date ? dateToApi(addDays(date, termsDays)) : "";
};

/**
 * The lines as tab-separated text, one per row, for pasting into a
 * spreadsheet or an external invoicing tool: label, quantity, unit,
 * unit price, VAT rate, amount. Numbers use a dot so they parse anywhere.
 */
export const linesToClipboardText = (
	lines: TInvoiceLine[],
	headers?: string[]
): string => {
	const { lines: computed } = computeInvoiceTotals(lines);
	const rows = computed.map((line) =>
		[
			line.label,
			String(line.quantity),
			line.unit,
			line.unitPrice.toFixed(2),
			line.vatApplicable && line.vatRate ? String(line.vatRate) : "0",
			line.amountHt.toFixed(2),
		].join("\t")
	);
	return [...(headers ? [headers.join("\t")] : []), ...rows].join("\n");
};

/** Whether a line is worth saving: something typed in it. */
export const isBlankLine = (line: TInvoiceLine): boolean =>
	!line.label.trim() &&
	!(line.description ?? "").trim() &&
	!line.unitPrice &&
	!line.unit.trim();
